'use client';

import { DEFAULT_COPILOT_MODE } from '@terra/shared-dto-chat-types';
import {
  useChatHasActiveNeoAttackTracking,
  type ChatRef,
} from '@terra/ui-design-system';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useSSE } from '../../app/providers/SseProvider';
import { useChatInstancesStore } from '../../store/chatInstancesStore';
import {
  countWorkingSubagents,
  hasComposerLoadingBackgroundExecution,
  useBackgroundExecutionStore,
} from '../../store/backgroundExecutionStore';
import { useConversations } from '../../store/conversations-store';
import {
  useSelectedTarget,
  useAppTargetId,
  useOptionalTargetId,
} from '../../store/projectStore';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';
import { type MentionsMenuComposerRef } from '.././mentions/MentionsMenuComposer';
import { useChatInstance } from '../../hooks/useChatInstance';
import { useBackgroundSubagentLifecycle } from '../../hooks/useBackgroundSubagentLifecycle';
import { getCopilotComposerUiState } from './copilotComposerUi';
import {
  hasChatActivity,
  shouldShowChatReconnecting,
} from '@terra/shared-utils';
import { isActiveChatInstanceStatus } from '../../utils/chatNetworkErrors';
import { useIsExternalToolChatActive } from '../../store/externalToolActivityStore';
import { resolveChatHistoryLoadStatus } from './ChatHistoryLoadState';
import { useChatTabsStore } from '../../store/chatTabsStore';
import {
  useCopilotModeSwitch,
  useRevertCopilotModeSwitch,
} from '../../hooks/useCopilotModeSwitch';
import {
  useCopilotPlan,
  useExecuteCopilotPlan,
} from '../../hooks/useCopilotPlan';
import { useCopilotPlanFileSync } from '../../hooks/useCopilotPlanFileSync';
import { useDebounceValue } from 'usehooks-ts';
import { useChatSavePreference } from './ChatSavePreferencePanel';
import { useSkillDraftsStore } from '../../store/skillDraftsStore';
import { useCopilotFindingEditProcessing } from './useCopilotFindingEditProcessing';
import { useChatAttachmentsController } from './ChatAttachmentsController';
import { readCompactionActivity } from './token-usage/compactionActivity';
import { useTokenUsageEstimate } from './token-usage/useTokenUsageEstimate';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useCoPilotDevTools } from './dev-tools/useCoPilotDevTools';
import { useCopilotMessageUpdateEffects } from './useCopilotMessageUpdateEffects';
import { useCopilotMainContentNavigation } from './useCopilotMainContentNavigation';
import { useCopilotDraftAndDropHandlers } from './useCopilotDraftAndDropHandlers';
import { useCopilotChatProviders } from './useCopilotChatProviders';
import { useCopilotChatEventHandlers } from './useCopilotChatEventHandlers';
import { useCopilotComposerSections } from './useCopilotComposerSections';
import { CopilotChatInstanceView } from './CopilotChatInstanceView';

type TaskContext = {
  applicationId: string | null;
  applicationUrl?: string | null;
};

const AGENT_ID = 'copilot';
const AGENT_AVATAR = '/images/terra-copilot.svg';

interface ChatInstanceProps {
  tabId: string;
  chatId: string;
  conversationId: string | null;
  isActive: boolean;
  skillDraftId?: string;
  linkedObject?: {
    kind: string;
    id: string;
    label?: string;
  };
  draftInput?: string;
  chatRef?: React.RefObject<ChatRef | null>;
  mentionsMenuRef?: React.RefObject<MentionsMenuComposerRef | null>;
}

export function ChatInstance({
  tabId,
  chatId: initialChatId,
  conversationId: initialConversationId,
  isActive,
  skillDraftId,
  linkedObject,
  draftInput,
  chatRef,
  mentionsMenuRef,
}: ChatInstanceProps) {
  const chatId = initialChatId;
  const chatComponentRef = useRef<ChatRef>(null);

  const [conversationId, setConversationId] = useState(initialConversationId);
  const [showEntryAnimation, setShowEntryAnimation] = useState(
    Boolean(initialConversationId),
  );
  const taskContextRef = useRef<TaskContext | undefined>(undefined);
  const getChatExecuteUrl = () =>
    `${getCurrentGatewayUrl()}/chat/graphs/co-pilot/invoke`;

  const { updateChatTab } = useChatTabsStore();
  const selectedTarget = useSelectedTarget();
  const targetId = useOptionalTargetId();
  const appTargetId = useAppTargetId();
  const { conversations, setActiveConversation } = useConversations({
    targetId,
  });
  const { subscribe } = useSSE();

  const currentSkillDraft = useSkillDraftsStore((state) =>
    skillDraftId ? state.drafts[skillDraftId] : undefined,
  );

  const tAgent = useTranslations('chat.copilot.agent');
  const agentDefinition = useMemo(
    () => ({
      id: AGENT_ID,
      name: tAgent('name'),
      role: tAgent('role'),
      avatar: AGENT_AVATAR,
      description: tAgent('description'),
      skills: [
        tAgent('skills.penetrationTestingGuidance'),
        tAgent('skills.securityAnalysis'),
        tAgent('skills.vulnerabilityDiscovery'),
        tAgent('skills.externalNetworkMapping'),
        tAgent('skills.securityTestingAutomation'),
      ],
    }),
    [tAgent],
  );
  const tCopilot = useTranslations('chat.copilot');
  const tSkillEditor = useTranslations('customSkills.editor');

  const savePreference = useChatSavePreference(appTargetId);
  const attachments = useChatAttachmentsController({
    threadId: chatId,
    targetId,
  });
  const providers = useCopilotChatProviders({ chatId, appTargetId });

  taskContextRef.current = {
    applicationId: targetId || null,
    applicationUrl: selectedTarget?.url || null,
  };

  const [editingQueuedMessageId, setEditingQueuedMessageId] = useState<
    string | null
  >(null);

  const instance = useChatInstance({
    chatId,
    conversationId,
    targetId,
    taskContextRef,
    getChatExecuteUrl,
    chatComponentRef,
    editingQueuedMessageId,
    skillDraftId,
    linkedObject,
    onQueuedMessageEditComplete: () => setEditingQueuedMessageId(null),
  });

  const messages = instance.messages;
  const devTools = useCoPilotDevTools();
  const tokenUsageEstimate = useTokenUsageEstimate(messages);
  const compactionActivity = useMemo(
    () => readCompactionActivity(messages),
    [messages],
  );
  const { isFeatureFlagEnabled: devToolsEnabled } = useFeatureFlags(
    'COPILOT_DEV_TOOLS_ENABLED',
  );

  useBackgroundSubagentLifecycle({ chatId });

  const navigation = useCopilotMainContentNavigation({
    chatId,
    linkedObject,
  });

  const draftAndDrop = useCopilotDraftAndDropHandlers({
    tabId,
    targetId,
    conversationId,
    isActive,
    status: instance.status,
    draftInput,
    chatComponentRef,
    uploadFiles: attachments.uploadFiles,
  });

  useCopilotMessageUpdateEffects({
    chatId,
    tabId,
    appTargetId,
    skillDraftId,
    messages,
    openTab: navigation.openTab,
    tSkillEditor,
  });

  const suppressPendingHITLLoading = useBackgroundExecutionStore((state) =>
    state.composerLoadingSuppressedChatIds.has(chatId),
  );
  const hasPendingBackgroundExecution = useBackgroundExecutionStore((state) =>
    hasComposerLoadingBackgroundExecution({
      executions: state.pendingExecutionsByToolCallId.values(),
      chatId,
    }),
  );
  const hasActiveExternalToolWork = useIsExternalToolChatActive(chatId);
  const isReconnecting = shouldShowChatReconnecting({
    isResumingStream: instance.isResumingStream,
    status: instance.status,
  });
  const streamIsLoading =
    isActiveChatInstanceStatus(instance.status) ||
    instance.isResumingStream ||
    hasPendingBackgroundExecution ||
    hasActiveExternalToolWork;
  const neoAttackTracking = useChatHasActiveNeoAttackTracking(messages);
  const isLoading =
    streamIsLoading || neoAttackTracking.hasActiveNeoAttackTracking;
  const generatingIndicatorLoading =
    isActiveChatInstanceStatus(instance.status) || isReconnecting;
  const workingCount = useBackgroundExecutionStore((state) => {
    void state.pendingRevision;

    return countWorkingSubagents({
      executions: state.pendingExecutionsByToolCallId.values(),
      chatId,
    });
  });
  const composerUi = getCopilotComposerUiState({
    isParentBusy:
      instance.status === 'streaming' || instance.status === 'submitted',
    workingCount,
  });

  const skillDraftReference = useMemo(() => {
    if (!skillDraftId) {
      return undefined;
    }

    const mode: 'edit' | 'create' = skillDraftId.startsWith('edit-')
      ? 'edit'
      : 'create';
    const skillId =
      mode === 'edit' ? skillDraftId.slice('edit-'.length) : undefined;

    return {
      type: 'skill-draft' as const,
      id: skillDraftId,
      label:
        currentSkillDraft?.name ||
        currentSkillDraft?.slug ||
        tSkillEditor('createTitle'),
      mode,
      skillId,
    };
  }, [
    currentSkillDraft?.name,
    currentSkillDraft?.slug,
    skillDraftId,
    tSkillEditor,
  ]);

  useCopilotModeSwitch({
    chatId,
    messages,
    isReplayingHistory: instance.isLoadingHistory,
  });

  const handleRevertCopilotModeSwitch = useRevertCopilotModeSwitch(chatId);

  useCopilotFindingEditProcessing({
    chatId,
    isActive,
    messages,
    isLoadingHistory: instance.isLoadingHistory,
    linkedObject,
    navigateInActiveTab: navigation.navigateInActiveTab,
    revealMainContentIfDetachedCopilot:
      navigation.revealMainContentIfDetachedCopilot,
    findingDetailsTabTitle: navigation.findingDetailsTabTitle,
  });

  useCopilotPlanFileSync({ chatId, messages });

  const { latestPlan } = useCopilotPlan({ chatId, messages });

  const keepChatVisibleDuringHistoryLoad = useChatInstancesStore((state) => {
    const chat = state.chats.get(chatId);
    const chatStatus = state.getChatStatus(chatId);

    return (
      hasChatActivity(chat?.messages) || isActiveChatInstanceStatus(chatStatus)
    );
  });
  const historyLoadStatus = resolveChatHistoryLoadStatus({
    isExistingConversation: Boolean(conversationId),
    isLoadingHistory: instance.isLoadingHistory,
    historyLoadError: instance.historyLoadError,
    keepChatVisibleDuringHistoryLoad,
  });

  const [shouldShowFeedback, setShouldShowFeedback] = useDebounceValue(
    false,
    500,
  );

  useEffect(() => {
    setShouldShowFeedback(
      instance.status === 'ready' &&
        messages?.some(
          (message) => !!message.id && message.role === 'assistant',
        ),
    );
  }, [instance.status, messages, setShouldShowFeedback]);

  useEffect(() => {
    if (!conversationId && conversations.length > 0) {
      const matchingConversation = conversations.find(
        (conv) => conv.threadId === chatId,
      );

      if (matchingConversation) {
        setConversationId(matchingConversation.id);
        updateChatTab(tabId, {
          conversationId: matchingConversation.id,
          title: matchingConversation.title || 'Chat',
        });

        if (isActive) {
          setActiveConversation(matchingConversation.id);
        }
      }
    }
  }, [
    conversations,
    chatId,
    conversationId,
    setActiveConversation,
    isActive,
    tabId,
    updateChatTab,
  ]);

  useEffect(() => {
    if (!chatRef) return;
    chatRef.current = isActive ? chatComponentRef.current : null;
  }, [chatRef, isActive, chatComponentRef]);

  const handlers = useCopilotChatEventHandlers({
    chatId,
    tabId,
    chatRef,
    chatComponentRef,
    editingQueuedMessageId,
    setEditingQueuedMessageId,
    appliedDraftInputRef: draftAndDrop.appliedDraftInputRef,
    sentDraftTextRef: draftAndDrop.sentDraftTextRef,
    sendMessage: instance.sendMessage,
    sendUserMessageWithResult: instance.sendUserMessageWithResult,
    handleSend: instance.handleSend,
    sendQueuedMessageNow: instance.sendQueuedMessageNow,
    requestBranchAskUserResend: instance.requestBranchAskUserResend,
    streamError: instance.error,
    isIntentionalAbort: instance.isIntentionalAbort,
    resumeStream: instance.resumeStream,
    regenerate: instance.regenerate,
    hasUploadingAttachments: attachments.hasUploadingAttachments,
    peekPendingAttachments: attachments.peekPendingAttachments,
    clearPendingAttachments: attachments.clearPendingAttachments,
    restorePendingAttachments: attachments.restorePendingAttachments,
  });

  const currentChatMode = useChatInstancesStore(
    (state) => state.configs.get(chatId)?.mode ?? DEFAULT_COPILOT_MODE,
  );
  const storedChatMode = useChatInstancesStore(
    (state) => state.configs.get(chatId)?.mode,
  );
  const executePlan = useExecuteCopilotPlan(chatId);

  const { branchAskUserDataMessage, composerTopContent } =
    useCopilotComposerSections({
      chatId,
      messages,
      targetId,
      targetType: selectedTarget?.targetType,
      appTargetId,
      editingQueuedMessageId,
      setEditingQueuedMessageId,
      chatComponentRef,
      instance,
      navigation,
      handlers,
      devToolsEnabled,
      devTools,
      tokenUsageEstimate,
      compactionActivity,
    });

  return (
    <CopilotChatInstanceView
      tabId={tabId}
      chatId={chatId}
      isActive={isActive}
      appTargetId={appTargetId}
      targetId={targetId}
      showEntryAnimation={showEntryAnimation}
      onClearEntryAnimation={() => setShowEntryAnimation(false)}
      handleChatDragOver={draftAndDrop.handleChatDragOver}
      handleChatDrop={draftAndDrop.handleChatDrop}
      handleChatPaste={draftAndDrop.handleChatPaste}
      historyLoadStatus={historyLoadStatus}
      historyLoadError={instance.historyLoadError}
      retryHistoryLoad={instance.retryHistoryLoad}
      showChat={historyLoadStatus === null}
      chatComponentRef={chatComponentRef}
      messages={messages}
      isLoading={isLoading}
      streamIsLoading={streamIsLoading}
      hasActiveNeoAttackTracking={neoAttackTracking.hasActiveNeoAttackTracking}
      neoAttackTrackingIdentity={neoAttackTracking.neoAttackTrackingIdentity}
      generatingIndicatorLoading={generatingIndicatorLoading}
      isReconnecting={isReconnecting}
      isCompacting={compactionActivity.isCompacting && streamIsLoading}
      compactionSummaryText={
        streamIsLoading ? compactionActivity.streamingSummary : null
      }
      suppressPendingHITLLoading={suppressPendingHITLLoading}
      messagePlaceholder={
        composerUi.showFollowUpPlaceholder
          ? tCopilot('followUpPlaceholder')
          : 'Message'
      }
      handleSendWithAttachments={handlers.handleSendWithAttachments}
      onEditUserMessage={instance.onEditUserMessage}
      onEditAskUserAnswer={instance.onEditAskUserAnswer}
      inlineEdit={instance.inlineEdit}
      checkpoint={instance.checkpoint}
      handleComposerInputChange={draftAndDrop.handleComposerInputChange}
      agentDefinition={agentDefinition}
      handleDataMessageEvent={handlers.handleDataMessageEvent}
      handleHITLDataMessageEvent={handlers.handleHITLDataMessageEvent}
      handleRetry={handlers.handleRetry}
      handleAbort={instance.stop}
      handleAbortSubagent={instance.abortSubagent}
      isSubmitDisabled={
        !!branchAskUserDataMessage ||
        instance.isApplyingBranch ||
        attachments.isUploadingAttachments
      }
      allowEmptySubmit={attachments.hasPendingAttachments}
      mentionsMenuRef={mentionsMenuRef}
      subscribe={subscribe}
      shouldShowFeedback={shouldShowFeedback}
      navigation={navigation}
      currentChatMode={currentChatMode}
      storedChatMode={storedChatMode}
      executePlan={executePlan}
      handleRevertCopilotModeSwitch={handleRevertCopilotModeSwitch}
      hasLatestPlan={Boolean(latestPlan)}
      handleTerminalAlwaysAllow={handlers.handleTerminalAlwaysAllow}
      skillDraftReference={skillDraftReference}
      loadMoreMessages={instance.loadMoreMessages}
      hasMoreMessages={instance.hasMoreMessages}
      isLoadingMoreMessages={instance.isLoadingMoreMessages}
      composerTopContent={composerTopContent}
      attachmentsPanel={attachments.attachmentsPanel}
      isNewChat={!conversationId}
      handlePromptSuggestionClick={handlers.handlePromptSuggestionClick}
      savePreference={savePreference}
      devToolsEnabled={devToolsEnabled}
      activeTool={devTools.activeTool}
      toggleTool={devTools.toggleTool}
      closeTool={devTools.closeTool}
      isTokenUsageOpen={devTools.isTokenUsageOpen}
      isFilesystemOpen={devTools.isFilesystemOpen}
      isAgentMessagesOpen={devTools.isAgentMessagesOpen}
      tokenUsageEstimate={tokenUsageEstimate}
      workingCount={workingCount}
      uploadFiles={attachments.uploadFiles}
      isBranchConfirmOpen={instance.isBranchConfirmOpen}
      isApplyingBranch={instance.isApplyingBranch}
      branchConfirmVariant={instance.branchConfirmVariant}
      confirmBranchResend={instance.confirmBranchResend}
      cancelBranchResend={instance.cancelBranchResend}
      handleBranchConfirmOpenChange={instance.handleBranchConfirmOpenChange}
      fileUploadMenu={attachments.fileUploadMenu}
      isStreamingStatus={isActiveChatInstanceStatus(instance.status)}
      providers={providers}
      imagePreviewUrls={attachments.imagePreviewUrls}
    />
  );
}
